/**
 * Каталог error-кодов. Используется и сервером, и клиентом для i18n + UX.
 * Добавляйте сюда новые коды только если действительно требуется отдельная
 * обработка на клиенте (или отдельный i18n-ключ).
 */
export const ERROR_CODES = {
  // Authentication / authorization
  AUTH_INVALID_CREDENTIALS: 'auth.invalid_credentials',
  AUTH_TOKEN_EXPIRED: 'auth.token_expired',
  AUTH_TOKEN_INVALID: 'auth.token_invalid',
  AUTH_2FA_REQUIRED: 'auth.2fa_required',
  AUTH_2FA_INVALID: 'auth.2fa_invalid',
  AUTH_LOCKED: 'auth.locked',
  AUTH_IP_BLOCKED: 'auth.ip_blocked',
  AUTH_FORBIDDEN: 'auth.forbidden',
  AUTH_PASSWORD_WEAK: 'auth.password_weak',

  // Workspace / multi-tenancy
  WORKSPACE_MISMATCH: 'workspace.mismatch',
  WORKSPACE_NOT_FOUND: 'workspace.not_found',
  WORKSPACE_ARCHIVED: 'workspace.archived',

  // Validation
  VALIDATION_FAILED: 'validation.failed',
  VALIDATION_TYPE_MISMATCH: 'validation.type_mismatch',

  // Rate limit / quota
  RATE_LIMITED: 'rate.limited',
  QUOTA_EXCEEDED: 'quota.exceeded',
  PLAN_LIMIT_REACHED: 'plan.limit_reached',

  // Idempotency
  IDEMPOTENCY_KEY_CONFLICT: 'idempotency.conflict',

  // Resources
  RESOURCE_NOT_FOUND: 'resource.not_found',
  RESOURCE_CONFLICT: 'resource.conflict',
  RESOURCE_GONE: 'resource.gone',

  // Tickets
  TICKET_INVALID_TRANSITION: 'ticket.invalid_transition',
  TICKET_LOCKED: 'ticket.locked',

  // Channels
  CHANNEL_VERIFICATION_FAILED: 'channel.verification_failed',
  CHANNEL_DISCONNECTED: 'channel.disconnected',

  // Связка с внешними платформами
  /** В переданном ключе канала не хватает полей — в details.missing их список */
  PARTNER_CREDENTIALS_INCOMPLETE: 'partner.credentials_incomplete',
  /** Ключ полон, но сам мессенджер/маркетплейс его не принял */
  PARTNER_CREDENTIALS_REJECTED: 'partner.credentials_rejected',
  /** Внешняя платформа не ответила или ответила ошибкой */
  PARTNER_UNAVAILABLE: 'partner.unavailable',

  // Bot
  BOT_FLOW_INVALID: 'bot.flow_invalid',
  BOT_API_CALL_BLOCKED_DOMAIN: 'bot.api_call.blocked_domain',
  BOT_API_CALL_BLOCKED_IP: 'bot.api_call.blocked_ip',
  BOT_API_CALL_TIMEOUT: 'bot.api_call.timeout',
  BOT_API_CALL_TOO_LARGE: 'bot.api_call.too_large',

  // Widget
  WIDGET_DOMAIN_NOT_ALLOWED: 'widget.domain_not_allowed',

  // Billing
  PAYMENT_REQUIRED: 'billing.payment_required',
  PAYMENT_FAILED: 'billing.payment_failed',

  // License (self-hosted)
  LICENSE_INVALID: 'license.invalid',
  LICENSE_EXPIRED: 'license.expired',
  LICENSE_DOMAIN_MISMATCH: 'license.domain_mismatch',
  LICENSE_OPERATORS_LIMIT: 'license.operators_limit',

  // GDPR
  GDPR_REQUEST_PENDING: 'gdpr.request_pending',

  // Internal
  INTERNAL_ERROR: 'internal.error',
  NOT_IMPLEMENTED: 'internal.not_implemented',
  SERVICE_UNAVAILABLE: 'internal.service_unavailable',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/** Маппинг error code → HTTP status. */
export const ERROR_HTTP_STATUS: Readonly<Record<ErrorCode, number>> = {
  [ERROR_CODES.AUTH_INVALID_CREDENTIALS]: 401,
  [ERROR_CODES.AUTH_TOKEN_EXPIRED]: 401,
  [ERROR_CODES.AUTH_TOKEN_INVALID]: 401,
  [ERROR_CODES.AUTH_2FA_REQUIRED]: 401,
  [ERROR_CODES.AUTH_2FA_INVALID]: 401,
  [ERROR_CODES.AUTH_LOCKED]: 423,
  [ERROR_CODES.AUTH_IP_BLOCKED]: 403,
  [ERROR_CODES.AUTH_FORBIDDEN]: 403,
  [ERROR_CODES.AUTH_PASSWORD_WEAK]: 422,

  [ERROR_CODES.WORKSPACE_MISMATCH]: 400,
  [ERROR_CODES.WORKSPACE_NOT_FOUND]: 404,
  [ERROR_CODES.WORKSPACE_ARCHIVED]: 410,

  [ERROR_CODES.VALIDATION_FAILED]: 422,
  [ERROR_CODES.VALIDATION_TYPE_MISMATCH]: 422,

  [ERROR_CODES.RATE_LIMITED]: 429,
  [ERROR_CODES.QUOTA_EXCEEDED]: 429,
  [ERROR_CODES.PLAN_LIMIT_REACHED]: 402,

  [ERROR_CODES.IDEMPOTENCY_KEY_CONFLICT]: 422,

  [ERROR_CODES.RESOURCE_NOT_FOUND]: 404,
  [ERROR_CODES.RESOURCE_CONFLICT]: 409,
  [ERROR_CODES.RESOURCE_GONE]: 410,

  [ERROR_CODES.TICKET_INVALID_TRANSITION]: 409,
  [ERROR_CODES.TICKET_LOCKED]: 423,

  [ERROR_CODES.CHANNEL_VERIFICATION_FAILED]: 401,
  [ERROR_CODES.CHANNEL_DISCONNECTED]: 503,

  [ERROR_CODES.PARTNER_CREDENTIALS_INCOMPLETE]: 422,
  [ERROR_CODES.PARTNER_CREDENTIALS_REJECTED]: 422,
  [ERROR_CODES.PARTNER_UNAVAILABLE]: 502,

  [ERROR_CODES.BOT_FLOW_INVALID]: 422,
  [ERROR_CODES.BOT_API_CALL_BLOCKED_DOMAIN]: 403,
  [ERROR_CODES.BOT_API_CALL_BLOCKED_IP]: 403,
  [ERROR_CODES.BOT_API_CALL_TIMEOUT]: 504,
  [ERROR_CODES.BOT_API_CALL_TOO_LARGE]: 413,

  [ERROR_CODES.WIDGET_DOMAIN_NOT_ALLOWED]: 403,

  [ERROR_CODES.PAYMENT_REQUIRED]: 402,
  [ERROR_CODES.PAYMENT_FAILED]: 402,

  [ERROR_CODES.LICENSE_INVALID]: 401,
  [ERROR_CODES.LICENSE_EXPIRED]: 402,
  [ERROR_CODES.LICENSE_DOMAIN_MISMATCH]: 403,
  [ERROR_CODES.LICENSE_OPERATORS_LIMIT]: 402,

  [ERROR_CODES.GDPR_REQUEST_PENDING]: 409,

  [ERROR_CODES.INTERNAL_ERROR]: 500,
  [ERROR_CODES.NOT_IMPLEMENTED]: 501,
  [ERROR_CODES.SERVICE_UNAVAILABLE]: 503,
};
